import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect, useRef } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useU2000QuickSearchListEffect } from '../../u2000Core/hooks/useU2000QuickSearchListEffect';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';

import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3152Context } from '../U3152Context';
import { doDeleteData, doSearch, luHandleAppiError, u3152FormatContactTypeListLabel, type U3152ContactTypeSearchRow } from '../U3152Service';
import { NS } from '../U3152';
import U3152ContactTypeDeleteDlg from './common/U3152ContactTypeDeleteDlg';
import { U3152ContactTypeListToolBar } from './list/U3152ContactTypeListToolBar';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from '../../u2000Core/tools/U2000QuickSearchUtils';

function parseBoolGridValue(value: unknown): boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (typeof o.value === 'boolean') return o.value;
  }
  return null;
}

function getColumnsDef(t: TFunction, language: string): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'contactTypeCode',
      label: t('lblContactTypeCode', { ns: NS }),
      widthClass: 'w-[180px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      display: (_column, _value, row: U3152ContactTypeSearchRow) => u3152FormatContactTypeListLabel(row, language),
    },
    {
      name: 'active',
      label: t('lblActive', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'string',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        const b = parseBoolGridValue(value);
        if (b === null) return '';
        return b ? t('lblYes', { ns: NS }) : t('lblNo', { ns: NS });
      },
    },
    {
      name: 'contactTypeNameFr',
      label: t('lblContactTypeNameFr', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      isDefaultSortedColumn: false,
    },
    {
      name: 'contactTypeNameEn',
      label: t('lblContactTypeNameEn', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: false,
      isDefaultSortedColumn: false,
    },
    {
      name: 'version',
      label: t('lblVersion', { ns: NS }),
      widthClass: 'w-[90px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'createdAt',
      label: t('lblFldCreatedAt', { ns: NS }),
      widthClass: 'w-[150px]',
      isDefaultColumn: true,
      type: 'date',
      inGridField: true,
      sortable: true,
      includeConditions: ['equals', 'notequals', 'greaterthan', 'greaterthanorequal', 'lesthan', 'lesthanorequal', 'interval'],
      isCriteriaField: true,
    },
    {
      name: 'deletedAt',
      label: t('lblCndFieldDeletedAt', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'date',
      inGridField: true,
      align: 'center',
      display: (_column, value) => {
        return value ? (
          <span className="font-bold text-red-600">{t('lblRowSoftDeleted', { ns: NS })}</span>
        ) : (
          ''
        );
      },
      includeConditions: ['isNotNull'],
      ovverideLabelConditions: [
        {
          name: 'isNotNull',
          label: t('lblCndDeletedAt', { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}

const U3152ContactTypeList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3152Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  useEffect(() => {
    const listTitle = t('title', { ns: NS });
    const language = i18n.resolvedLanguage ?? i18n.language;
    searchStateHandlers.setColumns(getColumnsDef(t, language));
    gridStateHandlers.setColumns(getColumnsDef(t, language));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t, i18n.language, i18n.resolvedLanguage]);

  const handleSearch = useU2000SearchHandlers<U3152ContactTypeSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3152ContactTypeSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? '/U3152';
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom('');
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version });
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: row.contactTypeCode ?? row.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const u3152ListHandlerActions: U2000TableHandlers<U3152ContactTypeSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3152ContactTypeSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3152');
      navigate('new');
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3152');
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: '/U3152',
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3152ContactTypeDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- toolbar aligné U3106
          <U3152ContactTypeListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u3152ListHandlerActions as any} />
        }
        handlerActions={u3152ListHandlerActions}
        label={t('lblContactTypeList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </>
  );
};

export default U3152ContactTypeList;
